import React, { useState, useEffect } from 'react';
import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import { useDashboard, type DashboardData, type MonthlyPoint } from '../../features/analytics/api';
import { useCurrentUser } from '../../features/auth/api';
import { DonutChart } from './DonutChart';
import { STATUS_LABELS } from './statusMeta';
import './Dashboard.scss';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtDelta(delta: number): string {
  return delta >= 0 ? `+${delta}%` : `${delta}%`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    DELIVERED: 'badge--delivered',
    INVOICED: 'badge--delivered',
    PAID: 'badge--delivered',
    ENROUTE: 'badge--enroute',
    APPROVED: 'badge--approved',
    ASSIGNED: 'badge--assigned',
    READY_FOR_PICKUP: 'badge--assigned',
    PENDING: 'badge--pending',
    WAITLIST: 'badge--pending',
    REJECTED: 'badge--rejected',
    CUSTOMER_CANCELLED: 'badge--cancelled',
  };
  return <span className={`db-badge ${cls[status] ?? ''}`}>{STATUS_LABELS[status] ?? status}</span>;
}

function AnimatedNumber({ value }: { value: number }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const duration = 900;
    const step = value / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= value) {
        setDisplay(value);
        clearInterval(timer);
      } else {
        setDisplay(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value]);
  return <>{display.toLocaleString()}</>;
}

function SparkLine({ data, color = '#3b82f6' }: { data: number[]; color?: string }) {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const h = 32,
    w = 80;
  const points = data.map((v, i) => ({
    x: (i / (data.length - 1)) * w,
    y: h - ((v - min) / (max - min || 1)) * h,
  }));
  const linePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  const fillPoints = [...points.map((p) => `${p.x},${p.y}`), `${w},${h}`, `0,${h}`].join(' ');
  return (
    <svg width={w} height={h} className="db-sparkline">
      <polygon points={fillPoints} fill={color} opacity={0.12} />
      <polyline
        points={linePoints}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LoadVolumeChart({ data }: { data: MonthlyPoint[] }) {
  const maxLoads = Math.max(...data.map((d) => d.loads), 1);
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div className="db-load-chart">
      <div className="db-chart-bars">
        {data.map((d, i) => {
          const hTotal = (d.loads / maxLoads) * 100;
          return (
            <div
              key={`${d.month}-${d.year}`}
              className="db-bar-col"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {hovered === i && (
                <div className="db-bar-tooltip">
                  <div className="db-bar-tooltip-month">{d.month}</div>
                  <div className="db-bar-tooltip-value">{d.loads} loads</div>
                  <div className="db-bar-tooltip-loads">{d.delivered} delivered</div>
                </div>
              )}
              <div
                className="db-bar-stack"
                style={{
                  height: `${Math.max(hTotal, d.loads > 0 ? 2 : 0)}%`,
                  animationDelay: `${i * 60}ms`,
                }}
              >
                <div
                  className="db-bar-delivered"
                  style={{ height: `${d.loads > 0 ? (d.delivered / d.loads) * 100 : 0}%` }}
                />
              </div>
              <div className="db-bar-label">{d.month}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Heatmap({ data }: { data: number[][] }) {
  const maxVal = Math.max(...data.flat(), 1);
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const now = new Date();
  const weeks = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getTime() - (11 - i) * 7 * 24 * 60 * 60 * 1000);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });

  return (
    <div className="db-heatmap">
      <div className="db-heatmap-days">
        {DAYS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="db-heatmap-grid">
        {data.map((week, wi) => (
          <div key={wi} className="db-heatmap-col">
            <div className="db-heatmap-week-label">{weeks[wi]}</div>
            {week.map((val, di) => {
              const intensity = val / maxVal;
              return (
                <div
                  key={di}
                  className="db-heatmap-cell"
                  title={`${DAYS[di]} ${weeks[wi]}: ${val} loads`}
                  style={{
                    background:
                      val === 0 ? '#f1f5f9' : `rgba(59, 130, 246, ${0.15 + intensity * 0.85})`,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="db-heatmap-scale">
        <span>0</span>
        <div className="db-heatmap-gradient" />
        <span>{maxVal}</span>
        <span className="db-heatmap-scale-label">loads/day</span>
      </div>
    </div>
  );
}

// ─── KPI card config ─────────────────────────────────────────────────────────

function buildKpiCards(data: DashboardData) {
  return [
    {
      label: 'Active Loads',
      rawValue: data.kpis.activeLoads,
      delta: data.kpis.activeLoadsDelta,
      icon: '🚛',
    },
    {
      label: 'Loads This Month',
      rawValue: data.kpis.loadsThisMonth,
      delta: data.kpis.loadsThisMonthDelta,
      icon: '📋',
    },
    {
      label: 'Active Customers',
      rawValue: data.kpis.customerCount,
      delta: data.kpis.customerDelta,
      icon: '🏢',
    },
    {
      label: 'Delivered This Month',
      rawValue: data.kpis.deliveredThisMonth,
      delta: data.kpis.deliveredThisMonthDelta,
      icon: '✅',
    },
  ];
}

// ─── Skeletons ───────────────────────────────────────────────────────────────

function KpiSkeleton() {
  return (
    <div className="db-kpi-grid">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="db-kpi-card">
          <div className="skeleton" style={{ width: 44, height: 44, borderRadius: 8 }} />
          <div className="db-kpi-body">
            <div className="skeleton" style={{ width: 80, height: 12, marginBottom: 8 }} />
            <div className="skeleton" style={{ width: 100, height: 24, marginBottom: 8 }} />
            <div className="skeleton" style={{ width: 70, height: 12 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const { data: me } = useCurrentUser();
  const isOrgAdmin = me?.type === 'ORG_OWNER' || me?.type === 'ORG_ADMIN';
  const hasOrg = !!me?.organization && Object.keys(me.organization || {}).length > 0;

  const { data, isLoading } = useDashboard(isOrgAdmin && hasOrg);

  const orgName = (me as any)?.organization?.name ?? 'Your Organization';
  const now = new Date();
  const subtitle = `${orgName} · ${now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;

  const sparkData = data?.monthlyLoads.map((m) => m.loads) ?? [];

  if (!isOrgAdmin) {
    return (
      <div className="layout">
        <AppSidebar />
        <main className="db-main">
          <div className="card" style={{ maxWidth: 480, marginTop: 40 }}>
            <h2 className="card__title">Dashboard</h2>
            <p className="card__subtitle muted">Only organization admins can view analytics.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="layout">
      <AppSidebar />
      <main className="db-main">
        {/* Header */}
        <div className="db-header">
          <div>
            <h1 className="db-title">Operations Dashboard</h1>
            <p className="db-subtitle">{subtitle}</p>
          </div>
          <div className="db-header-right">
            <div className="db-live-badge">
              <span className="db-live-dot" />
              Live
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        {isLoading || !data ? (
          <KpiSkeleton />
        ) : (
          <div className="db-kpi-grid">
            {buildKpiCards(data).map((s, i) => (
              <div key={s.label} className="db-kpi-card" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="db-kpi-icon">{s.icon}</div>
                <div className="db-kpi-body">
                  <div className="db-kpi-label">{s.label}</div>
                  <div className="db-kpi-value">
                    <AnimatedNumber value={s.rawValue} />
                  </div>
                  <div
                    className={`db-kpi-delta ${s.delta >= 0 ? 'db-kpi-delta--up' : 'db-kpi-delta--down'}`}
                  >
                    {s.delta >= 0 ? '↑' : '↓'} {fmtDelta(s.delta)} vs last month
                  </div>
                </div>
                <SparkLine data={sparkData} color={s.delta >= 0 ? '#10b981' : '#ef4444'} />
              </div>
            ))}
          </div>
        )}

        {/* Load Volume chart + Donut */}
        <div className="db-row">
          <div className="db-card db-card--wide">
            <div className="db-card-header">
              <div>
                <div className="db-card-title">Monthly Load Volume</div>
                <div className="db-card-sub">
                  Loads created per month · last 12 mo ·{' '}
                  <span className="db-chart-legend-created">■</span> created{' '}
                  <span className="db-chart-legend-delivered">■</span> delivered
                </div>
              </div>
              {data && (
                <div className="db-card-stat">
                  <span className="db-card-stat-value">{data.kpis.loadsThisMonth} this month</span>
                  <span
                    className={`db-card-stat-delta ${data.kpis.loadsThisMonthDelta >= 0 ? 'db-card-stat-delta--up' : 'db-card-stat-delta--down'}`}
                  >
                    {fmtDelta(data.kpis.loadsThisMonthDelta)}
                  </span>
                </div>
              )}
            </div>
            {isLoading || !data ? (
              <div className="skeleton" style={{ height: 180 }} />
            ) : (
              <LoadVolumeChart data={data.monthlyLoads} />
            )}
          </div>

          <div className="db-card">
            <div className="db-card-header">
              <div>
                <div className="db-card-title">Load Status</div>
                <div className="db-card-sub">All-time breakdown</div>
              </div>
            </div>
            {isLoading || !data ? (
              <div className="skeleton" style={{ height: 180 }} />
            ) : (
              <DonutChart data={data.statusBreakdown} />
            )}
          </div>
        </div>

        {/* Top Products + Carriers */}
        <div className="db-row">
          <div className="db-card">
            <div className="db-card-header">
              <div className="db-card-title">Top Products</div>
              <span className="db-card-link">by load volume · last 12 mo</span>
            </div>
            {isLoading || !data ? (
              <div className="skeleton" style={{ height: 200 }} />
            ) : data.topProducts.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No product data yet.
              </p>
            ) : (
              <div className="db-product-list">
                {data.topProducts.map((p, i) => (
                  <div key={p.name} className="db-product-row">
                    <div className="db-product-rank">#{i + 1}</div>
                    <div className="db-product-info">
                      <div className="db-product-name">{p.name}</div>
                      <div className="db-product-bar-wrap">
                        <div
                          className="db-product-bar"
                          style={{ width: `${p.pct}%`, transitionDelay: `${i * 80}ms` }}
                        />
                      </div>
                    </div>
                    <div className="db-product-stats">
                      <span>{p.loads} loads</span>
                      <span className="db-product-rev">{p.units.toLocaleString()} units</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="db-card">
            <div className="db-card-header">
              <div className="db-card-title">Carrier Activity</div>
              <span className="db-card-link">by load count · last 12 mo</span>
            </div>
            {isLoading || !data ? (
              <div className="skeleton" style={{ height: 200 }} />
            ) : data.carrierPerformance.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No carrier data yet.
              </p>
            ) : (
              <div className="db-carrier-list">
                {(() => {
                  const maxLoads = data.carrierPerformance[0]?.loads ?? 1;
                  return data.carrierPerformance.map((c, ci) => {
                    const pct = Math.round((c.loads / maxLoads) * 100);
                    return (
                      <div key={c.name} className="db-carrier-row">
                        <div className="db-carrier-avatar">{c.name[0].toUpperCase()}</div>
                        <div className="db-carrier-info">
                          <div className="db-carrier-name">{c.name}</div>
                          <div className="db-carrier-sub">{c.loads} loads</div>
                        </div>
                        <div className="db-carrier-bar-wrap">
                          <div
                            className="db-carrier-bar"
                            style={{ width: `${pct}%`, transitionDelay: `${ci * 80}ms` }}
                          />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Heatmap */}
        <div className="db-card db-card--full">
          <div className="db-card-header">
            <div>
              <div className="db-card-title">Order Activity</div>
              <div className="db-card-sub">Orders created per day · last 12 weeks</div>
            </div>
          </div>
          {isLoading || !data ? (
            <div className="skeleton" style={{ height: 120 }} />
          ) : (
            <Heatmap data={data.heatmap} />
          )}
        </div>

        {/* Top Customers + Recent Loads */}
        <div className="db-row">
          <div className="db-card">
            <div className="db-card-header">
              <div className="db-card-title">Top Customers</div>
              <span className="db-card-link">by load count · last 12 mo</span>
            </div>
            {isLoading || !data ? (
              <div className="skeleton" style={{ height: 200 }} />
            ) : data.topCustomers.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No customer data yet.
              </p>
            ) : (
              <div className="db-customer-list">
                {data.topCustomers.map((c, i) => (
                  <div key={c.name} className="db-customer-row">
                    <div className="db-customer-rank">{i + 1}</div>
                    <div className="db-customer-info">
                      <div className="db-customer-name">{c.name}</div>
                      <div className="db-customer-loc">{c.location || '—'}</div>
                    </div>
                    <div className="db-customer-stats">
                      <div className="db-customer-rev">{c.loads} loads</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="db-card db-card--wide">
            <div className="db-card-header">
              <div className="db-card-title">Recent Loads</div>
            </div>
            {isLoading || !data ? (
              <div className="skeleton" style={{ height: 200 }} />
            ) : data.recentLoads.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No loads yet.
              </p>
            ) : (
              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th>Load #</th>
                      <th>Customer</th>
                      <th>Product</th>
                      <th>Carrier</th>
                      <th>Status</th>
                      <th>Qty</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentLoads.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <span className="db-load-num">{r.loadNumber ?? '—'}</span>
                        </td>
                        <td>{r.customer}</td>
                        <td className="db-table-muted">{r.product}</td>
                        <td className="db-table-muted">{r.carrier}</td>
                        <td>
                          <StatusBadge status={r.status} />
                        </td>
                        <td>{r.quantity.toLocaleString()}</td>
                        <td className="db-table-muted">{fmtDate(r.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
